import { youtubeEmbedUrl, youtubeVideoId } from "./youtube-id";
import type { JSX } from "react";
import { PlayCircle } from "lucide-react";
import { cn } from "../../lib/utils";

interface VideoProps {
  id?: string;
  className?: string;
  heading?: string;
  subheading?: string;
  /** A YouTube link in any common form, or a bare video id. */
  videoUrl?: string;
  caption?: string;
  /** In the editor the frame ignores clicks, so a click selects the section instead of playing. */
  isEditing?: boolean;
}

const SURFACE_RADIUS_STYLE = { borderRadius: "var(--jobsite-radius)" } as const;

/** What YouTube's player needs to play, go full screen and open its links; nothing else. */
const IFRAME_SANDBOX = "allow-scripts allow-same-origin allow-presentation allow-popups allow-popups-to-escape-sandbox";

/**
 * Whether a text prop holds something to show. The editors pass an element in place of the string.
 * @param {string | undefined} value The prop.
 * @returns {boolean} True when it is a non-empty string or an element.
 */
const isShown = (value: string | undefined): boolean => value !== undefined && value !== "";

/**
 * The frame's accessible title: the heading, else the caption, else "Video".
 * @param {VideoProps} props The section's props.
 * @returns {string} The title.
 */
const frameTitle = ({ heading, caption }: VideoProps): string => {
  if (typeof heading === "string" && heading !== "") {
    return heading;
  }
  if (typeof caption === "string" && caption !== "") {
    return caption;
  }
  return "Video";
};

/** The heading and subheading above the video, when either is set. */
const VideoHeader = ({ heading, subheading }: Pick<VideoProps, "heading" | "subheading">): JSX.Element | null => {
  if (!isShown(heading) && !isShown(subheading)) {
    return null;
  }
  return (
    <div className="jobsite:mx-auto jobsite:mb-12 jobsite:max-w-3xl jobsite:text-center">
      {isShown(heading) ? (
        <h2 className="jobsite:font-heading jobsite:text-3xl jobsite:tracking-tight jobsite:text-balance jobsite:text-foreground jobsite:sm:text-4xl jobsite:md:text-5xl">
          {heading}
        </h2>
      ) : null}
      {isShown(subheading) ? (
        <p className="jobsite:mt-4 jobsite:text-lg jobsite:text-pretty jobsite:text-muted-foreground jobsite:md:text-xl">
          {subheading}
        </p>
      ) : null}
    </div>
  );
};

/** Where the video goes before a link is pasted. Only the editor shows it. */
const VideoEmpty = (): JSX.Element => (
  <div
    data-testid="video-empty"
    className="jobsite:absolute jobsite:inset-0 jobsite:flex jobsite:flex-col jobsite:items-center jobsite:justify-center jobsite:gap-3 jobsite:px-6 jobsite:text-center jobsite:text-muted-foreground"
  >
    <PlayCircle aria-hidden="true" className="jobsite:h-10 jobsite:w-10" />
    <p className="jobsite:text-base">Paste a YouTube link in this section&apos;s settings.</p>
  </div>
);

/** One YouTube video in a 16:9 frame, under an optional heading. Renders nothing live without a valid link. */
const Video = (props: VideoProps): JSX.Element | null => {
  const { id, className, heading, subheading, videoUrl, caption, isEditing = false } = props;
  const videoId = youtubeVideoId(videoUrl);
  if (videoId === null && !isEditing) {
    return null;
  }
  return (
    <section id={id} className={cn("jobsite:py-16 jobsite:md:py-24 jobsite:bg-background", className)}>
      <div className="jobsite:container jobsite:mx-auto jobsite:px-4">
        <VideoHeader heading={heading} subheading={subheading} />
        <figure className="jobsite:mx-auto jobsite:max-w-4xl">
          <div
            className="jobsite:relative jobsite:aspect-video jobsite:w-full jobsite:overflow-hidden jobsite:bg-muted jobsite:shadow-sm"
            style={SURFACE_RADIUS_STYLE}
          >
            {videoId === null ? <VideoEmpty /> : (
              <iframe
                className={cn("jobsite:absolute jobsite:inset-0 jobsite:h-full jobsite:w-full", isEditing && "jobsite:pointer-events-none")}
                src={youtubeEmbedUrl(videoId)}
                title={frameTitle(props)}
                loading="lazy"
                sandbox={IFRAME_SANDBOX}
                allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
              />
            )}
          </div>
          {isShown(caption) ? (
            <figcaption className="jobsite:mt-4 jobsite:text-center jobsite:text-base jobsite:text-muted-foreground">
              {caption}
            </figcaption>
          ) : null}
        </figure>
      </div>
    </section>
  );
};

export type { VideoProps };
export { Video };
